type RatingScaleBound = 'from' | 'to';

interface RatingScaleValues {
    fromValue: string;
    toValue: string;
}

const RATING_SCALE_GAP = 2;
const RATING_SCALE_MAX = 10;
const FROM_MIN = 0;
const FROM_MAX = RATING_SCALE_MAX - RATING_SCALE_GAP;
const TO_MIN = RATING_SCALE_GAP;
const TO_MAX = RATING_SCALE_MAX;

const SOURCE_RANGES: Record<RatingScaleBound, { min: number; max: number }> = {
    from: { min: FROM_MIN, max: FROM_MAX },
    to: { min: TO_MIN, max: TO_MAX },
};

const clampToRange = ({ value, min, max }: { value: string; min: number; max: number }): string => {
    if (value === '') {
        return value;
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
        return value;
    }

    if (numericValue < min) {
        return String(min);
    }

    if (numericValue > max) {
        return String(max);
    }

    return value;
};

const computeOppositeBoundWithGap = ({
    sourceValue,
    oppositeValue,
    source,
}: {
    sourceValue: string;
    oppositeValue: string;
    source: RatingScaleBound;
}): string => {
    if (sourceValue === '' || oppositeValue === '') {
        return oppositeValue;
    }

    const sourceNumber = Number(sourceValue);
    const oppositeNumber = Number(oppositeValue);

    if (Number.isNaN(sourceNumber) || Number.isNaN(oppositeNumber)) {
        return oppositeValue;
    }

    if (source === 'from') {
        const minOpposite = sourceNumber + RATING_SCALE_GAP;
        return oppositeNumber < minOpposite ? String(minOpposite) : oppositeValue;
    }

    const maxOpposite = sourceNumber - RATING_SCALE_GAP;
    return oppositeNumber > maxOpposite ? String(maxOpposite) : oppositeValue;
};

const computeNextRatingScaleBounds = ({
    inputValue,
    source,
    fromValue,
    toValue,
}: {
    inputValue: string;
    source: RatingScaleBound;
    fromValue: string;
    toValue: string;
}): RatingScaleValues => {
    const range = SOURCE_RANGES[source];
    const clampedSourceValue = clampToRange({ value: inputValue, ...range });
    const oppositeValue = source === 'from' ? toValue : fromValue;
    const adjustedOppositeValue = computeOppositeBoundWithGap({
        sourceValue: clampedSourceValue,
        oppositeValue,
        source,
    });

    if (source === 'from') {
        return { fromValue: clampedSourceValue, toValue: adjustedOppositeValue };
    }

    return { fromValue: adjustedOppositeValue, toValue: clampedSourceValue };
};

export { computeNextRatingScaleBounds };
export type { RatingScaleBound, RatingScaleValues };
